import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '../api/client.js';
import { Modal } from '../components/Modal.js';
import { Alert, Button, Field, Select, TextArea } from '../components/ui.js';
import { EDP_REJECTION_REASONS } from '../../shared/employeeDocs.js';

interface ReturnEntry {
  reasonCode: string;
  comment: string;
}

/**
 * Pick documents, give a reason (and optional note) for each, and POST them to a
 * return endpoint that takes `{ items: [{ itemId, reasonCode, comment }] }`.
 */
export function ReturnItemsModal({
  endpoint,
  items,
  title,
  description,
  submitLabel,
  noteLabel,
  onClose,
  onDone,
}: {
  endpoint: string;
  items: readonly { id: string; name: string }[];
  title: string;
  description: string;
  submitLabel: (count: number) => string;
  noteLabel: string;
  onClose: () => void;
  onDone: () => Promise<void> | void;
}) {
  const [selected, setSelected] = useState<Record<string, ReturnEntry | undefined>>({});
  const [error, setError] = useState<string | null>(null);
  const chosen = items.filter((item) => selected[item.id]);
  const submit = useMutation({
    mutationFn: () =>
      apiRequest(endpoint, {
        method: 'POST',
        body: {
          items: chosen.map((item) => {
            const entry = selected[item.id] as ReturnEntry;
            return {
              itemId: item.id,
              reasonCode: entry.reasonCode,
              comment: entry.comment.trim() || undefined,
            };
          }),
        },
      }),
    onSuccess: () => onDone(),
    onError: (cause) =>
      setError(
        cause instanceof ApiRequestError ? cause.message : 'The documents could not be returned.',
      ),
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      description={description}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={chosen.length === 0}
            loading={submit.isPending}
            onClick={() => submit.mutate()}
          >
            {submitLabel(chosen.length)}
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="error" className="mb-3">
          {error}
        </Alert>
      )}
      <ul className="flex flex-col gap-3">
        {items.map((item) => {
          const entry = selected[item.id];
          return (
            <li key={item.id} className="rounded-md border border-slate-200 p-3">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-900">
                <input
                  type="checkbox"
                  checked={Boolean(entry)}
                  onChange={(event) =>
                    setSelected((current) => ({
                      ...current,
                      [item.id]: event.target.checked
                        ? { reasonCode: EDP_REJECTION_REASONS[0].code, comment: '' }
                        : undefined,
                    }))
                  }
                />
                {item.name}
              </label>
              {entry && (
                <div className="mt-3 flex flex-col gap-3">
                  <Field label="Reason" htmlFor={`return-reason-${item.id}`} required>
                    <Select
                      id={`return-reason-${item.id}`}
                      value={entry.reasonCode}
                      onChange={(event) =>
                        setSelected((current) => ({
                          ...current,
                          [item.id]: { ...entry, reasonCode: event.target.value },
                        }))
                      }
                    >
                      {EDP_REJECTION_REASONS.map((reason) => (
                        <option key={reason.code} value={reason.code}>
                          {reason.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label={noteLabel} htmlFor={`return-comment-${item.id}`}>
                    <TextArea
                      id={`return-comment-${item.id}`}
                      rows={2}
                      value={entry.comment}
                      onChange={(event) =>
                        setSelected((current) => ({
                          ...current,
                          [item.id]: { ...entry, comment: event.target.value },
                        }))
                      }
                    />
                  </Field>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}
